/**
 * REP-CASH (D115, D461): a figure the API says is not available yet is shown
 * as exactly that, never as a number and never as a zero.
 *
 * The API says so two ways, both keyed by code, never by English:
 *
 * - a whole report refused with 422 CAPABILITY_NOT_BUILT and
 *   `meta {capability, slice}` (the cash flow statement, on the Cash Flow
 *   tab);
 * - one figure inside a 200 replaced by
 *   `{status "not-available", code "CAPABILITY_NOT_BUILT", capability, slice}`
 *   (the dashboard's cash position).
 *
 * The words come from the `finance.notAvailable` catalog, chosen by the
 * capability code; the slice that will deliver the figure is the API's, a
 * parameter isolated left to right so an Arabic sentence keeps "AE-ST2"
 * whole. A capability this screen has no sentence for still says "not
 * available yet" and names the slice.
 *
 * It is a statement, not a failure: `role="status"`, no Try again (asking
 * again cannot change the answer), and the query is not retried.
 */
import { Card } from "@/components/ui";
import { ApiError } from "@/lib/api/client";
import { useTranslation } from "@/lib/i18n";
import { ltr } from "@/lib/i18n/format";

/** What is not available, and the build-plan slice that delivers it. */
export interface NotAvailable {
  capability: string;
  slice: string;
}

/** The catalog sentence for each capability code the API sends. */
const SENTENCES: Record<string, "cashFlowStatement" | "cashPosition"> = {
  "cash-flow-statement": "cashFlowStatement",
  "cash-position": "cashPosition",
};

const strings = (capability: unknown, slice: unknown): NotAvailable | null =>
  typeof capability === "string" &&
  capability !== "" &&
  typeof slice === "string" &&
  slice !== ""
    ? { capability, slice }
    : null;

/** A 422 CAPABILITY_NOT_BUILT refusal's capability and slice, else null. */
export function notAvailableOf(error: unknown): NotAvailable | null {
  if (!(error instanceof ApiError) || error.code !== "CAPABILITY_NOT_BUILT")
    return null;
  const meta = (error.extensions.meta ?? {}) as Record<string, unknown>;
  return strings(meta.capability, meta.slice);
}

/** A figure sent as the not-available state, else null. */
export function notAvailableFigure(value: unknown): NotAvailable | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (v.status !== "not-available" || v.code !== "CAPABILITY_NOT_BUILT")
    return null;
  return strings(v.capability, v.slice);
}

/** The sentence for `what`, in the active language. */
export function useNotAvailableText(what: NotAvailable): string {
  const { t } = useTranslation();
  const sentence = SENTENCES[what.capability];
  return sentence
    ? t(`finance.notAvailable.${sentence}` as const, { slice: ltr(what.slice) })
    : t("finance.notAvailable.other", { slice: ltr(what.slice) });
}

/** A report that is not available yet, in place of the report. */
export function NotAvailableReport({
  what,
  title,
}: {
  what: NotAvailable;
  title: string;
}) {
  const { t } = useTranslation();
  const text = useNotAvailableText(what);
  return (
    <Card title={title}>
      <div role="status">
        <p style={{ margin: 0, fontWeight: 600 }}>
          {t("finance.notAvailable.title")}
        </p>
        <p style={{ marginBlock: "8px 0", color: "var(--atlas-text)" }}>
          {text}
        </p>
      </div>
    </Card>
  );
}
